import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { getCanteenSettings, updateCanteenSettings } from '../../services/canteenSettingsService';
import { useAuth } from '../../context/AuthContext';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import Button from '../../components/ui/Button';
import TextInput from '../../components/ui/TextInput.jsx';
import CheckboxInput from '../../components/ui/CheckboxInput';

const Wrapper = styled.div``;

const Card = styled.div`
  background: #fff;
  border-radius: 6px;
  padding: 28px;
  box-shadow: 0 1px 4px rgba(0, 66, 66, 0.06);
  max-width: 560px;
  margin-top: 16px;
`;

const Title = styled.h3`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 15px; font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 16px;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 16px;
`;

export default function CanteenSettingsPage() {
  const { t } = useTranslation();
  const { canWrite } = useAuth();

  const [form, setForm] = useState({
    defaultDailyLimit: 2,
    defaultMonthlyLimit: 30,
    allowHolidayEntries: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLoading(true);
    getCanteenSettings()
      .then((res) => {
        const s = res.data?.data;
        if (s) setForm({
          defaultDailyLimit: s.defaultDailyLimit ?? 2,
          defaultMonthlyLimit: s.defaultMonthlyLimit ?? 30,
          allowHolidayEntries: !!s.allowHolidayEntries,
        });
      })
      .finally(() => setLoading(false));
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await updateCanteenSettings({
        defaultDailyLimit: Number(form.defaultDailyLimit) || 0,
        defaultMonthlyLimit: Number(form.defaultMonthlyLimit) || 0,
        allowHolidayEntries: !!form.allowHolidayEntries,
      });
    } catch { /* interceptor */ }
    setSaving(false);
  }, [form]);

  if (loading) return null;

  return (
    <Wrapper>
      <Breadcrumbs items={[{ label: t('canteen.settings') }]} />
      <Card>
        <Title>{t('canteen.settings')}</Title>
        <Row>
          <TextInput
            label={t('canteen.defaultDailyLimit')}
            type="number"
            value={form.defaultDailyLimit}
            onChange={(e) => setForm((p) => ({ ...p, defaultDailyLimit: e.target.value }))}
            disabled={!canWrite('CN')}
          />
          <TextInput
            label={t('canteen.defaultMonthlyLimit')}
            type="number"
            value={form.defaultMonthlyLimit}
            onChange={(e) => setForm((p) => ({ ...p, defaultMonthlyLimit: e.target.value }))}
            disabled={!canWrite('CN')}
          />
        </Row>
        <CheckboxInput
          label={t('canteen.allowHolidayEntries')}
          checked={form.allowHolidayEntries}
          onChange={(val) => setForm((p) => ({ ...p, allowHolidayEntries: val }))}
          disabled={!canWrite('CN')}
        />
        {canWrite('CN') && (
          <Actions>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? '...' : t('common.save')}
            </Button>
          </Actions>
        )}
      </Card>
    </Wrapper>
  );
}
